import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

/**
 * ⚠️  LEGAL REVIEW PENDING — see backend/docs/legal-review-checklist.md
 *
 * These Terms of Service were drafted in-house and have NOT been reviewed by
 * a UK commercial / SaaS / AI-experienced solicitor. Known gaps that need
 * specialist drafting:
 *
 *   - Customer indemnity for claims arising from the customer's use of
 *     report content (defamation, breach of confidence, etc.)
 *   - Defamation / accuracy disclaimer linked to in-report disclaimer
 *   - Acceptable use policy (no minors, no harassment, no use against
 *     ongoing legal proceedings without counsel involvement)
 *   - Refund policy split: Stripe vs crypto (chargebacks possible vs not)
 *   - "Digital content immediate supply" waiver of UK 14-day cancellation
 *     right under Consumer Contracts Regulations 2013, with explicit
 *     consent captured at checkout
 *   - Liability cap (typically capped at fees paid in last 12 months)
 *   - Governing law (England and Wales) and jurisdiction
 *   - Account termination triggers
 *   - Agent API terms (separate B2B product) — distinct or rolled in?
 *
 * If you bump the legally-meaningful content of these terms, also bump
 * CURRENT_TERMS_VERSION in backend/middleware/policyVersion.js (TBD)
 * and User.termsAcceptedVersion will be re-checked at next login.
 */
const TERMS_VERSION = 1;

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Terms of Service — id100" description="The terms governing use of id100's due-diligence platform." path="/terms" />
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />Back
          </Link>
          <Logo size="sm" />
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
          <p className="text-gray-500 mb-6">Last updated: April 2026</p>
          
          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Agreement to Terms</h2>
              <p>By accessing or using the Due Diligence Platform provided by Totalinfo Ltd ("we", "our", "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Description of Services</h2>
              <p>We provide an AI-powered due diligence platform that generates investigative reports on individuals and businesses. Our services include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>AI-assisted due diligence report generation</li>
                <li>Risk assessment and analysis</li>
                <li>Document export in various formats</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
              <p>To use our services, you must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be at least 18 years of age</li>
                <li>Register for an account with accurate information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorised access</li>
              </ul>
              <p className="mt-3">You are responsible for all activities that occur under your account.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Acceptable Use</h2>
              <p>You agree to use our services only for lawful purposes. You must not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use reports for illegal discrimination or harassment</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Submit false or misleading information</li>
                <li>Attempt to circumvent security measures</li>
                <li>Use the service to harm or defraud others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Payment Terms</h2>
              <p><strong>Pay-As-You-Go:</strong> Single reports are charged at the displayed rate at the time of purchase. Payment is required before report generation.</p>
              <p className="mt-3"><strong>Subscriptions:</strong> Subscription fees are billed monthly or annually in advance. You may cancel at any time, with access continuing until the end of the billing period.</p>
              <p className="mt-3"><strong>Refunds:</strong> Due to the nature of AI-generated reports, refunds are generally not available once a report has been generated. Please contact us if you believe there has been an error.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Intellectual Property</h2>
              <p className="bg-green-50 p-4 rounded-md">
                <strong>Your Content:</strong> You retain ownership of all data you submit and all reports generated from your data. The inputs you provide and the outputs (reports) generated remain your property.
              </p>
              <p className="mt-3"><strong>Our Platform:</strong> The platform, including its design, features, and underlying technology, remains our intellectual property.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Nature of Research Outputs</h2>
              <p className="bg-yellow-50 p-4 rounded-md border-l-4 border-yellow-400">
                <strong>Important Notice:</strong> Reports produced by this platform are research outputs compiled by artificial intelligence systems drawing upon open source intelligence gathered from a wide array of publicly accessible data sets and global information resources. They represent a synthesis of research findings and are expressly not offered as fit for any particular purpose.
              </p>
              <p className="mt-4">
                Whilst we employ sophisticated multi-agent AI research systems to compile as broad and thorough a picture as the publicly available record permits, the reports produced may contain errors, inaccuracies, or omissions — whether arising from the limitations of AI and machine learning technologies, gaps or inaccuracies within publicly available information, or the inherent imprecision of open source research methods. We make no representation as to the accuracy, completeness, currency, or reliability of any information contained within a report.
              </p>
              <p className="mt-3">
                By purchasing and accessing any report, you accept and acknowledge the following conditions in full:
              </p>
              <ul className="list-disc pl-6 space-y-3 mt-3">
                <li>
                  <strong>Reports are research outputs, not authoritative findings.</strong> The information contained in any report is derived from publicly available sources and compiled using AI-assisted research methods. It should be treated as a starting point for further enquiry rather than a definitive or conclusive account. You are responsible for independently verifying any information you intend to rely upon.
                </li>
                <li>
                  <strong>Reports are not professional advice.</strong> Nothing in any report constitutes legal, financial, medical, regulatory, employment, or any other form of professional advice. Reports must not be treated as a substitute for consultation with appropriately qualified professionals in any regulated or specialist field.
                </li>
                <li>
                  <strong>Reports may contain errors and are not warranted fit for purpose.</strong> We offer reports as research outputs compiled from publicly accessible sources and open source intelligence. They are provided without any warranty — express or implied — as to accuracy, completeness, fitness for any particular purpose, or freedom from error. You accept the information entirely at your own risk.
                </li>
                <li>
                  <strong>Reports must not be the primary basis for significant decisions affecting identifiable persons.</strong> Where a report relates to an identifiable individual or entity, it must not be used as the sole or determining basis for decisions that could have a material legal or practical consequence for that person or organisation. This includes, without limitation, decisions concerning employment, creditworthiness, housing, insurance, healthcare, education, regulatory action, or access to services. Any such decisions must be reached through your own independent assessment and, where appropriate, with the benefit of qualified professional advice.
                </li>
                <li>
                  <strong>You bear full responsibility for your use of report content.</strong> By accepting these Terms you agree that you assume sole and entire responsibility for any decisions made, actions taken, or consequences arising — whether directly or indirectly — from your use of or reliance upon information contained in any report generated by this platform. Totalinfo Ltd accepts no liability whatsoever for any loss, damage, claim, regulatory sanction, or other adverse outcome arising from such use.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Data Processing</h2>
              <p>By using our services, you acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your data is processed by third-party AI providers</li>
                <li>Your data is NOT used for AI model training</li>
                <li>Data processing is subject to our Privacy Policy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Limitation of Liability</h2>
              <p>To the maximum extent permitted by law:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We provide our services "as is" without warranties of any kind</li>
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability shall not exceed the amount paid by you in the preceding 12 months</li>
                <li>We are not responsible for decisions made based on report content</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Indemnification</h2>
              <p>You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the services or violation of these terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Termination</h2>
              <p>We may suspend or terminate your account if you breach these terms. Upon termination:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to the platform will cease</li>
                <li>You may request export of your data</li>
                <li>Unpaid fees become immediately due</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Changes to Terms</h2>
              <p>We may update these terms from time to time. Your continued use of the platform after any changes constitutes acceptance of the updated terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">13. Governing Law</h2>
              <p>These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of Northern Ireland.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">14. Contact</h2>
              <p>For questions about these Terms of Service:</p>
              <p className="mt-2">
                <strong>Totalinfo Ltd</strong><br />
                Email: <a href="mailto:legal@totalinfo.net" className="text-red-600">legal@totalinfo.net</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
